// Tracks the last pointer position and resolves the email row physically under
// the cursor at keypress time. Keyboard shortcuts target "whatever row I'm
// hovering over right now" — reading the live DOM via elementFromPoint is robust
// to scrolling and to browsers that don't refresh :hover until the mouse moves,
// unlike a hover store updated only on mouseenter/leave (audit §2.17 #5).

let lastClientX: number | null = null;
let lastClientY: number | null = null;

/** Record the latest pointer coordinates (called from the global mousemove listener). */
export function recordPointerPosition(x: number, y: number): void {
  lastClientX = x;
  lastClientY = y;
}

/** Forget the pointer position (e.g. on listener teardown). */
export function clearPointerPosition(): void {
  lastClientX = null;
  lastClientY = null;
}

export type PointerTarget =
  | { type: 'email'; ids: string[] }
  | { type: 'group'; ids: string[]; groupKey: string | null };

/**
 * The row physically under the pointer right now, or null. Reads the live DOM
 * so it reflects the row under the cursor *now*, even after scrolling since
 * the last mouseenter. Resolves either a single email row or a sender-group
 * row (with its group key, so the caller can act on the WHOLE sender). An
 * individual row wins over a group when both match (more specific).
 */
export function getPointerTarget(): PointerTarget | null {
  if (typeof document === 'undefined' || lastClientX === null || lastClientY === null) {
    return null;
  }
  const el = document.elementFromPoint(lastClientX, lastClientY);
  if (!el) {
    return null;
  }

  const row = el.closest<HTMLElement>('[data-email-row-id]');
  if (row?.dataset.emailRowId) {
    return { type: 'email', ids: [row.dataset.emailRowId] };
  }

  const group = el.closest<HTMLElement>('[data-sender-group-email-ids]');
  if (group) {
    const ids = (group.dataset.senderGroupEmailIds ?? '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    if (ids.length) {
      return { type: 'group', ids, groupKey: group.dataset.senderGroupKey ?? null };
    }
  }

  return null;
}

/** Back-compat id-only view of getPointerTarget. */
export function getEmailIdsUnderPointer(): string[] | null {
  const target = getPointerTarget();
  return target?.ids.length ? target.ids : null;
}
